const VIDEO_CAP_MS = 20000;

class ImageVideoSlider extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll(".ivs_tab"));
    this.fills = Array.from(this.querySelectorAll("[data-fill]"));
    this.slides = Array.from(this.querySelectorAll(".ivs_slide"));
    this.trackViewport = this.querySelector("[data-track-viewport]");
    this.nav = this.querySelector(".ivs_nav");

    if (!this.tabs.length || !this.slides.length || !this.trackViewport) return;

    this.autoplayEnabled = this.dataset.autoplay === "true";
    this.autoplaySpeed = Number(this.dataset.autoplaySpeed) || 5000;
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (this.reducedMotion) this.autoplayEnabled = false;

    this.dragEnabled = this.dataset.drag === "true";

    this.activeIndex = 0;
    this.timer = null;
    this.fillAnimation = null;

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.selectTab(Number(tab.dataset.index)));
    });

    this.nav.addEventListener("keydown", (event) => this.handleKeydown(event));


    this.setupReveal();
    this.setupDrag();


    this._onResize = () => this.updateTrackPosition();
    window.addEventListener("resize", this._onResize);

    this.activateSlide(0);
  }

  disconnectedCallback() {
    this.clearTimer();
    this.cancelFill();
    if (this._onResize) window.removeEventListener("resize", this._onResize);
   
    if (this.revealObserver) this.revealObserver.disconnect();
    
  }


  setupReveal() {
    if (!("IntersectionObserver" in window) || this.reducedMotion) return;

    this.classList.add("ivs--will-reveal");

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.classList.add("is-in-view");
          this.revealObserver.disconnect();
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" },
    );

    this.revealObserver.observe(this);
  }


  setupDrag() {
    if (!this.dragEnabled) return;

    const viewport = this.querySelector(".ivs_viewport");
    if (!viewport || this.slides.length < 2) return;

    this.classList.add("ivs--draggable");

    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let delta = 0;
    let dragging = false;

    const baseOffset = () => {
      const slide = this.slides[this.activeIndex];
      return slide ? -slide.offsetLeft : 0;
    };

    const onDown = (event) => {
      if (!event.isPrimary || event.button > 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      delta = 0;
      dragging = false;
    };

    const onMove = (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;

      const moveX = event.clientX - startX;
      const moveY = event.clientY - startY;

      if (!dragging) {
        if (Math.abs(moveX) < 8) return;
        if (Math.abs(moveY) > Math.abs(moveX)) {
          pointerId = null;
          return;
        }
        dragging = true;
        this.classList.add("is-dragging");
        if (viewport.setPointerCapture) viewport.setPointerCapture(pointerId);
      }

      delta = moveX;
      this.trackViewport.style.transform = `translateX(${baseOffset() + delta}px)`;
    };

    const onUp = (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;

      const travelled = delta;
      const moved = dragging;

      if (viewport.releasePointerCapture && viewport.hasPointerCapture(pointerId)) {
        viewport.releasePointerCapture(pointerId);
      }

      pointerId = null;
      dragging = false;
      delta = 0;
      this.classList.remove("is-dragging");

      if (!moved) return;

      this.suppressClick = true;
      window.setTimeout(() => {
        this.suppressClick = false;
      }, 0);

      const threshold = Math.max(60, viewport.clientWidth * 0.12);
      const lastIndex = this.slides.length - 1;
      let next = this.activeIndex;

      if (travelled <= -threshold) next = Math.min(this.activeIndex + 1, lastIndex);
      else if (travelled >= threshold) next = Math.max(this.activeIndex - 1, 0);

      if (next === this.activeIndex) this.updateTrackPosition();
      else this.activateSlide(next);
    };

    viewport.addEventListener("pointerdown", onDown);
    viewport.addEventListener("pointermove", onMove);
    viewport.addEventListener("pointerup", onUp);
    viewport.addEventListener("pointercancel", onUp);
    viewport.addEventListener("dragstart", (event) => event.preventDefault());
    viewport.addEventListener(
      "click",
      (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true,
    );
  }

  handleKeydown(event) {
    const key = event.key;
    if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End"].includes(key)) return;
    event.preventDefault();

    let index = this.activeIndex;
    if (key === "ArrowUp" || key === "ArrowLeft") index = (index - 1 + this.tabs.length) % this.tabs.length;
    if (key === "ArrowDown" || key === "ArrowRight") index = (index + 1) % this.tabs.length;
    if (key === "Home") index = 0;
    if (key === "End") index = this.tabs.length - 1;

    this.goTo(index);
    this.tabs[index].focus();
  }

  goTo(index) {
    if (index === this.activeIndex) return;
    this.activateSlide(index);
  }

  selectTab(index) {
    this.activateSlide(index);
  }

  activateSlide(index) {
    this.activeIndex = index;

    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle("is-active", isActive);
      tab.setAttribute("aria-selected", String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle("is-active", isActive);
      slide.setAttribute("aria-hidden", String(!isActive));

      const link = slide.querySelector(".ivs_link[href]");
      if (link) link.tabIndex = isActive ? 0 : -1;
    });

    this.updateTrackPosition();
    this.scrollActiveTabIntoView(index);
    this.updateVideoPlayback(index);
    this.restartProgress(index);
  }

 
  scrollActiveTabIntoView(index) {
    if (this.nav.scrollWidth <= this.nav.clientWidth + 1) return;

    const item = this.tabs[index]?.closest(".ivs_nav_item");
    if (!item) return;

    const target = item.offsetLeft - (this.nav.clientWidth - item.offsetWidth) / 2;
    const left = Math.max(0, Math.min(target, this.nav.scrollWidth - this.nav.clientWidth));
    this.nav.scrollTo({ left, behavior: this.reducedMotion ? "auto" : "smooth" });
  }

  updateTrackPosition() {
    const activeSlide = this.slides[this.activeIndex];
    if (!activeSlide) return;
    this.trackViewport.style.transform = `translateX(-${activeSlide.offsetLeft}px)`;
  }

  updateVideoPlayback(index) {
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector("[data-slide-video]");
      if (!video) return;

      if (i === index) {
        if (!video.dataset.capBound) {
          video.dataset.capBound = "true";
          video.addEventListener("timeupdate", () => {
            if (video.currentTime >= VIDEO_CAP_MS / 1000) video.currentTime = 0;
          });
        }
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  slideDuration(index) {
    const isVideo = this.slides[index]?.dataset.mediaType === "video";
    return isVideo ? Math.min(this.autoplaySpeed, VIDEO_CAP_MS) : this.autoplaySpeed;
  }

  restartProgress(index) {
    this.clearTimer();
    this.cancelFill();

    this.fills.forEach((fill) => {
      fill.getAnimations().forEach((animation) => animation.cancel());
      fill.style.transform = "scaleX(0)";
    });

    const activeFill = this.fills[index];
    const duration = this.slideDuration(index);

    if (activeFill && !this.reducedMotion) {
      this.fillAnimation = activeFill.animate(
        [{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }],
        { duration, easing: "linear", fill: "forwards" },
      );
    } else if (activeFill) {
      activeFill.style.transform = "scaleX(1)";
    }

    if (this.autoplayEnabled) this.scheduleAdvance(duration);
  }

  scheduleAdvance(duration) {
    this.clearTimer();
    this.timer = window.setTimeout(() => {
      this.activateSlide((this.activeIndex + 1) % this.tabs.length);
    }, duration);
  }

  clearTimer() {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  cancelFill() {
    if (this.fillAnimation) {
      this.fillAnimation.cancel();
      this.fillAnimation = null;
    }
  }
}

customElements.define("image-video-slider", ImageVideoSlider);
